import React, { useState, useEffect } from 'react';
import {
  Modal, ModalHeader, ModalBody, ModalFooter, Button,
  FormGroup, Label, Input
} from 'reactstrap';
import { supabase } from '../../supabaseClient';
import {
  FiMessageSquare, FiPlus, FiTrash2,
  FiStar, FiUser
} from 'react-icons/fi';

const EMPTY = { title: '', description: '' };

const ReviewsManager = () => {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);
  const [selItem, setSelItem]     = useState(null);
  const [success, setSuccess]     = useState('');
  const [error, setError]         = useState('');

  const fetch = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setRows(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openAdd = () => {
    setForm(EMPTY); setModal(true); setError(''); setSuccess('');
  };
  const openDel = (r) => { setSelItem(r); setDeleteModal(true); setError(''); setSuccess(''); };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) return;
    setSaving(true);
    setError('');
    try {
      const { error } = await supabase.from('reviews').insert(form);
      if (error) throw error;
      setSuccess('Review added successfully!');
      setModal(false);
      fetch();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selItem) return;
    try {
      const { error } = await supabase.from('reviews').delete().eq('id', selItem.id);
      if (error) throw error;
      setSuccess('Review deleted successfully!');
      setDeleteModal(false);
      setSelItem(null);
      fetch();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={s.wrap}>
      <div style={s.hdr}>
        <div>
          <h2 style={s.title}><FiMessageSquare style={{ marginRight: 10 }} />Reviews</h2>
          <p style={s.sub}>{rows.length} testimonials · Client reviews</p>
        </div>
        <button style={s.addBtn} onClick={openAdd}>
          <FiPlus size={16} /> Add Review
        </button>
      </div>

      {success && (
        <div style={s.successBanner}>✓ {success}</div>
      )}
      {error && (
        <div style={s.errorBanner}>⚠ {error}</div>
      )}

      {loading ? (
        <div style={s.center}><span style={s.spinner} /></div>
      ) : rows.length === 0 ? (
        <div style={s.empty}>
          <FiStar size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p>No reviews yet. Add your first testimonial.</p>
        </div>
      ) : (
        <div style={s.list}>
          {rows.map(r => (
            <div key={r.id} style={s.card}>
              <div style={s.cardLeft}>
                <div style={s.avatar}>
                  <FiUser size={20} />
                </div>
              </div>
              <div style={s.cardBody}>
                <div style={s.cardTitle}>{r.title}</div>
                <p style={s.cardDesc}>{r.description}</p>
              </div>
              <div style={s.cardActions}>
                <button style={{ ...s.editBtn, ...s.delBtn }} onClick={() => openDel(r)} title="Delete">
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="md">
        <ModalHeader toggle={() => setModal(false)}>
          <FiPlus style={{ marginRight: 8 }} />Add Review
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="rev-title" style={{ fontWeight: 600 }}>
              Title <span style={{ color: '#ef4444' }}>*</span>
            </Label>
            <Input
              id="rev-title"
              type="text"
              placeholder="Title"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            />
          </FormGroup>
          <FormGroup>
            <Label for="rev-desc" style={{ fontWeight: 600 }}>
              Description <span style={{ color: '#ef4444' }}>*</span>
            </Label>
            <Input
              id="rev-desc"
              type="textarea"
              rows={5}
              placeholder="Write the review content here…"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              style={{ resize: 'vertical' }}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)}>Cancel</Button>
          <Button
            color="primary"
            onClick={handleSave}
            disabled={!form.title.trim() || !form.description.trim() || saving}
            style={{ background: 'linear-gradient(135deg,#7c3aed,#ec4899)', border: 'none' }}
          >
            {saving ? 'Saving…' : 'Add Review'}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered size="sm">
        <ModalHeader toggle={() => setDeleteModal(false)}>
          <FiTrash2 style={{ marginRight: 8, color: '#ef4444' }} />Delete Review
        </ModalHeader>
        <ModalBody>
          <p style={{ margin: 0 }}>
            Delete review "<strong>{selItem?.title}</strong>"? This cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDeleteModal(false)}>Cancel</Button>
          <Button color="danger" onClick={handleDelete}>Delete</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const s = {
  wrap: { padding: '8px 0' },
  hdr:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center' },
  sub:   { margin: '4px 0 0', opacity: 0.5, fontSize: 13 },
  addBtn: {
    background: 'linear-gradient(135deg,#7c3aed,#ec4899)',
    color: '#fff', border: 'none', borderRadius: 10,
    padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14,
    display: 'flex', alignItems: 'center', gap: 6,
    boxShadow: '0 4px 16px rgba(124,58,237,0.35)',
  },
  successBanner: {
    background: 'rgba(16, 185, 129, 0.12)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
    borderRadius: 10, padding: '10px 16px',
    color: '#6ee7b7', fontSize: 13, marginBottom: 20,
  },
  errorBanner: {
    background: 'rgba(239, 68, 68, 0.12)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: 10, padding: '10px 16px',
    color: '#fca5a5', fontSize: 13, marginBottom: 20,
  },
  list: { display: 'flex', flexDirection: 'column', gap: 14 },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 14,
    padding: '18px 20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    transition: 'border-color 0.2s',
  },
  cardLeft: { flexShrink: 0 },
  avatar: {
    width: 44, height: 44, borderRadius: '50%',
    background: 'linear-gradient(135deg,#7c3aed44,#ec489944)',
    border: '1px solid rgba(124,58,237,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#a78bfa',
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardTitle: { fontWeight: 700, fontSize: 15, marginBottom: 6 },
  cardDesc:  { margin: 0, opacity: 0.6, fontSize: 14, lineHeight: 1.6,
    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  cardActions: { display: 'flex', gap: 8, flexShrink: 0 },
  editBtn: {
    background: 'rgba(255,255,255,0.07)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, padding: '7px 10px',
    cursor: 'pointer', color: 'inherit',
    transition: 'background 0.15s',
  },
  delBtn: { background: 'rgba(239,68,68,0.12)', borderColor: 'rgba(239,68,68,0.25)', color: '#ef4444' },
  center: { display: 'flex', justifyContent: 'center', padding: 60 },
  spinner: {
    width: 36, height: 36,
    border: '3px solid rgba(255,255,255,0.1)',
    borderTopColor: '#7c3aed',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },
  empty: { textAlign: 'center', padding: '60px 20px', opacity: 0.5 },
};

export default ReviewsManager;
